<script setup lang="ts">
import { watch, reactive } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { PersonStores } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { ClassroomInterface } from '@/types/User';

const toast = useToast();

const profileStore = PersonStores.useUserProfileStore();

const general: ClassroomInterface = reactive({
  zoom_classroom: undefined,
});

const urlRegex =
  /^(?:(?:(?:https):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;

const safeUrl = (value: string): boolean => urlRegex.test(value);

const rules = {
  zoom_classroom: { safeUrl: helpers.withMessage('The value is not a valid URL address', safeUrl) },
};

const v$ = useVuelidate(rules, general);

const doToast = (error = false) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? 'The zoom classroom must be a valid URL.' : 'Classroom link has been saved',
    life: 5000,
  });
};

const onSave = async () => {
  if (!v$.value.$error) {
    resetDirty();
    await profileStore.updateGeneralInfo({ ...general });
    doToast();
  }
};

watch(
  () => profileStore.general?.zoom_classroom,
  (value) => {
    general.zoom_classroom = value;
  },
);

const resetDirty = () => {
  v$.value.$reset();
};

const onCancel = () => {
  general.zoom_classroom = profileStore.general?.zoom_classroom;
  resetDirty();
};
</script>

<template>
  <FormCard
    title="Classroom"
    :dirty="v$.$anyDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="!can([PERMISSIONS.PEOPLE_UPDATE]) || (v$.zoom_classroom.$anyDirty && v$.zoom_classroom.$invalid)"
  >
    <template #form>
      <form class="w-full">
        <div class="field">
          <InputText
            id="zoom_classroom"
            v-model="v$.zoom_classroom.$model"
            type="url"
            class="inputfield w-full"
            :class="{ 'p-invalid': v$.zoom_classroom.$invalid && v$.zoom_classroom.$dirty }"
            :disabled="!can([PERMISSIONS.PEOPLE_UPDATE])"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>
